import type { ActivityLogicPlugin } from '../../core/types/plugin.base.types';
import type { WallPayload, WallRuntime } from '../../core/types/payloads/wall.types';
import type { PluginContext } from '../../core/types/plugin.base.types';
import type { Participant, RoutingMode } from '../../core/types/room.types';
import type { HostActionPayload, StudentInputEnvelope } from '../../core/types/events.types';

const DEFAULT_STANCES = [
  { id: 'for', label: 'A favor', color: '#22c55e' },
  { id: 'against', label: 'En contra', color: '#ef4444' },
];

export function validateWallPayload(raw: unknown): WallPayload {
  if (!raw || typeof raw !== 'object') throw new Error('Wall payload must be an object');
  const data = raw as Partial<WallPayload>;
  if (!data.title) throw new Error('Wall requires title');
  const stances = Array.isArray(data.stances) && data.stances.length >= 2
    ? data.stances.map((s, i) => ({
        id: String((s as { id?: string }).id || `stance_${i + 1}`),
        label: String((s as { label?: string }).label || `Postura ${i + 1}`),
        color: String((s as { color?: string }).color || DEFAULT_STANCES[i % 2].color),
      }))
    : DEFAULT_STANCES;
  return {
    title: String(data.title),
    prompt: String(data.prompt || 'Comparte tu argumento'),
    maxChars: Number(data.maxChars) || 140,
    stances,
  };
}

export function createInitialWallRuntime(
  _payload: WallPayload,
  _routing: RoutingMode,
): WallRuntime {
  return { posts: {}, highlightedPostId: undefined };
}

export const wallLogic: ActivityLogicPlugin<'wall'> = {
  mode: 'wall',

  validatePayload: validateWallPayload,
  createInitialRuntime: createInitialWallRuntime,

  onStudentInput(ctx, input: StudentInputEnvelope, participant: Participant) {
    if (input.action !== 'POST') {
      return { runtime: ctx.runtime };
    }
    if (ctx.phase !== 'ACTIVE') {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'NOT_ACTIVE' };
    }
    const data = input.data as { text?: string; stanceId?: string } | undefined;
    const text = String(data?.text ?? '').trim().slice(0, ctx.payload.maxChars ?? 140);
    const stanceId = String(data?.stanceId ?? '');
    if (!text) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'EMPTY_POST' };
    }
    if (!ctx.payload.stances.some((s) => s.id === stanceId)) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'INVALID_STANCE' };
    }
    if (ctx.runtime.posts[participant.participantId]) {
      return { runtime: ctx.runtime, rejected: true, rejectReason: 'ALREADY_POSTED' };
    }

    const posts = {
      ...ctx.runtime.posts,
      [participant.participantId]: {
        participantId: participant.participantId,
        text,
        stanceId,
        createdAt: Date.now(),
      },
    };
    return { runtime: { ...ctx.runtime, posts } };
  },

  onHostAction(ctx, action: HostActionPayload) {
    if (action.action === 'HIGHLIGHT_POST') {
      const postId = String((action.data as { participantId?: string })?.participantId ?? '');
      return {
        runtime: {
          ...ctx.runtime,
          highlightedPostId: postId || undefined,
        },
      };
    }
    if (action.action === 'CLEAR_HIGHLIGHT') {
      return { runtime: { ...ctx.runtime, highlightedPostId: undefined } };
    }
    return { runtime: ctx.runtime };
  },
};

export function demoWallPayload(): WallPayload {
  return {
    title: 'Debate en el aula',
    prompt: '¿Deberían los celulares estar prohibidos durante el recreo?',
    maxChars: 140,
    stances: [
      { id: 'for', label: 'A favor', color: '#22c55e' },
      { id: 'against', label: 'En contra', color: '#ef4444' },
      { id: 'neutral', label: 'Depende', color: '#eab308' },
    ],
  };
}
